import * as XLSX from "xlsx";
import type { ParseResult } from "../types";

/**
 * Build an XLSX workbook (as bytes) from a parsed statement.
 * Amounts are written as real numbers so spreadsheets treat them as numeric;
 * a summary block records the reconciliation outcome for auditability.
 */
export function toXlsx(result: ParseResult): Uint8Array {
  const hasBalance = result.transactions.some((t) => t.balance != null);
  const hasValueDate = result.transactions.some((t) => t.valueDate != null);

  const header: string[] = ["Date"];
  if (hasValueDate) header.push("Value date");
  header.push("Description", `Amount (${result.meta.currency})`);
  if (hasBalance) header.push(`Balance (${result.meta.currency})`);

  const rows: (string | number)[][] = [header];
  for (const t of result.transactions) {
    const row: (string | number)[] = [t.date];
    if (hasValueDate) row.push(t.valueDate ?? "");
    row.push(t.description, round2(t.amount));
    if (hasBalance) row.push(t.balance != null ? round2(t.balance) : "");
    rows.push(row);
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = header.map((h, i) => ({
    wch: i === header.indexOf("Description") ? 40 : Math.max(h.length + 2, 12),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Transactions");

  // Second sheet: reconciliation + metadata (the trust artifact).
  const r = result.reconciliation;
  const summary: (string | number)[][] = [
    ["Bank", result.meta.bankName],
    ["Country", result.meta.country],
    ["Currency", result.meta.currency],
    ["Account", result.meta.accountId ?? ""],
    ["Period", `${result.meta.periodStart ?? "?"} — ${result.meta.periodEnd ?? "?"}`],
    ["Transactions", result.transactions.length],
    [],
    ["Opening balance", r.openingBalance ?? ""],
    ["Sum of transactions", round2(result.transactions.reduce((a, t) => a + t.amount, 0))],
    ["Computed closing", r.computedClosing ?? ""],
    ["Stated closing", r.closingBalance ?? ""],
    ["Difference", r.difference ?? ""],
    ["Reconciled", r.ok ? "YES" : "NO"],
  ];
  const summaryWs = XLSX.utils.aoa_to_sheet(summary);
  summaryWs["!cols"] = [{ wch: 22 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new Uint8Array(out);
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
